import React, { useContext, useEffect, useState } from 'react';
import { Box, Typography, Button, Stack, useTheme, CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import useJoinTest from '../../hooks/useJoinTest';
import { ThemeContext } from '../../globalContext/theme/themeProvider';
import { styles } from './TestCountdownBannerStyles';
import useFetch from '../../services/apiService/useFetch';
import API_ENDPOINTS, { API_VERSION } from '../../consts/endpoint';
import { TestList, TestsListResponse } from '../../interface';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import isBetween from 'dayjs/plugin/isBetween';
import { SESSION_TYPE_STRING } from '../../consts/constants';
import ModeEditOutlineRoundedIcon from '@mui/icons-material/ModeEditOutlineRounded';
import editIcon from '../../assets/images/edit-icon.svg';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isBetween);

type Props = {
    onVisibilityChange?: (isVisible: boolean) => void;
};

const TestCountdownBanner: React.FC<Props> = ({ onVisibilityChange }) => {
    const { handleJoinTest, isLoading: isGettingToken } = useJoinTest();
    const [timeLeft, setTimeLeft] = useState({ hours: '00', minutes: '00', seconds: '00' });
    const [isTestLive, setIsTestLive] = useState(false);
    const [shouldShowBanner, setShouldShowBanner] = useState(false);
    const theme = useTheme();
    const [themeState] = useContext(ThemeContext);
    const isHaleBrand = themeState?.brand?.key === 'hale';
    const classes = styles(theme, isHaleBrand);
    const { t } = useTranslation();

    // Ref to prevent duplicate API calls
    const hasFetchedRef = React.useRef(false);

    // Fetch upcoming tests - only call once on mount
    const { data: upcomingTestsData, isLoading: upcomingTestsLoading, fetchData } = useFetch<TestsListResponse>({
        url: API_ENDPOINTS.UPCOMING_TESTS,
        otherOptions: {
            apiVersion: API_VERSION.V2,
            skipApiCallOnMount: true,
        },
    });

    // Fetch data only once on component mount
    useEffect(() => {
        if (!hasFetchedRef.current) {
            hasFetchedRef.current = true;
            fetchData({ stateUpdateNotRequired: false });
        }
    }, [fetchData]);

    // Filter tests to only show those with assessment_status 0 or 1
    const filteredTests = upcomingTestsData?.data?.filter(
        (test: TestList) => test.assessment_status === 0 || test.assessment_status === 1
    ) || [];

    // Get the first upcoming test from filtered list
    const upcomingTest: TestList | null = filteredTests[0] || null;
    const testType = upcomingTest?.type || 0;

    // Timer and visibility check - runs every second for real-time updates
    useEffect(() => {
        if (!upcomingTest?.start_time) {
            setShouldShowBanner(false);
            return;
        }

        const updateTimer = () => {
            const now = dayjs().tz('Asia/Kolkata');
            const startTime = dayjs.utc(upcomingTest.start_time).tz('Asia/Kolkata');
            const endTime = dayjs.utc(upcomingTest.end_time).tz('Asia/Kolkata');

            // Hide banner if test has ended (current time is after end time)
            if (now.isAfter(endTime)) {
                setShouldShowBanner(false);
                setIsTestLive(false);
                return;
            }

            // Check if we should show the banner (after midnight of test day and before end time)
            const testDate = startTime.format('YYYY-MM-DD');
            const midnightOfTestDay = dayjs.tz(testDate, 'Asia/Kolkata').startOf('day');
            const showBanner = (now.isAfter(midnightOfTestDay) || now.isSame(midnightOfTestDay)) && now.isBefore(endTime);
            setShouldShowBanner(showBanner);

            // Check if test is live (between start and end time)
            const testIsLive = now.isBetween(startTime, endTime, null, '[)');
            setIsTestLive(testIsLive);

            if (testIsLive) {
                // Show 00:00:00 when test is live
                setTimeLeft({ hours: '00', minutes: '00', seconds: '00' });
                return;
            }

            // Calculate time until test starts
            const distance = startTime.diff(now, 'millisecond');

            if (distance <= 0) {
                setTimeLeft({ hours: '00', minutes: '00', seconds: '00' });
                return;
            }

            const hours = String(Math.floor(distance / (1000 * 60 * 60))).padStart(2, '0');
            const minutes = String(Math.floor((distance / (1000 * 60)) % 60)).padStart(2, '0');
            const seconds = String(Math.floor((distance / 1000) % 60)).padStart(2, '0');

            setTimeLeft({ hours, minutes, seconds });
        };

        updateTimer();
        const interval = setInterval(updateTimer, 1000);
        return () => clearInterval(interval);
    }, [upcomingTest]);

    // Notify parent component about visibility
    useEffect(() => {
        const hasValidAssessmentStatus = upcomingTest?.assessment_status === 0 || upcomingTest?.assessment_status === 1;
        const isVisible = !upcomingTestsLoading && !!upcomingTest && shouldShowBanner && hasValidAssessmentStatus;
        onVisibilityChange?.(isVisible);
    }, [upcomingTestsLoading, upcomingTest, shouldShowBanner, onVisibilityChange]);

    // Don't show banner if no upcoming test, loading, before midnight of test day, or test has ended
    const hasValidAssessmentStatus = upcomingTest?.assessment_status === 0 || upcomingTest?.assessment_status === 1;
    if (upcomingTestsLoading || !upcomingTest || !shouldShowBanner || !hasValidAssessmentStatus) {
        return null;
    }

    // Check if test button should be shown (only when test is live and assessment_status is 0 or 1)
    const shouldShowButton = isTestLive && hasValidAssessmentStatus;

    // Determine button text: Start Test (assessment_id == 0) or Resume Test
    const isNewTest = upcomingTest.assessment_id === 0;
    const buttonText = isNewTest ? t('tests.startTest') : t('tests.resumeTest');

    const handleTestAction = () => {
        handleJoinTest({
            sessionId: testType === 1 ? upcomingTest.session_id || 0 : upcomingTest.test_id || 0,
            sessionType: testType === 1 ? SESSION_TYPE_STRING.PROCTOR_TEST : SESSION_TYPE_STRING.NON_PROCTOR_TEST,
        });
    };

    return (
        <Box sx={{ ...classes.bannerWrapper }}>
            <Box sx={{ ...classes.bannerContent }}>
                <Stack 
                    direction="row" 
                    alignItems="center" 
                    spacing={{ xs: 0.75, sm: 1, md: 2 }}
                    sx={{
                        flex: 1,
                        minWidth: 0,
                    }}
                >
                    <Box sx={{ ...classes.iconBox }}>
                       <img src={editIcon} alt="Edit Icon" />
                    </Box>
                    <Box sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        flex: 1,
                        minWidth: 0,
                    }}>
                        <Typography sx={{ ...classes.bannerLightHeading }}>
                            Join Test
                        </Typography>
                        <Typography sx={{ ...classes.bannerDarkHeading }}>
                            {upcomingTest.test_name || upcomingTest.test_title}
                        </Typography>
                    </Box>
                </Stack>

                <Stack 
                    direction="row" 
                    alignItems="center" 
                    spacing={{ xs: 0.75, sm: 1, md: 2 }}
                    sx={{
                        '@media(max-width: 768px)': {
                            justifyContent: 'flex-end',
                            flexWrap: 'nowrap',
                        },
                    }}
                >
                    {!isTestLive && (
                        <>
                            <Typography sx={{ ...classes.timeLeftText }}>
                                Few Time Left:
                            </Typography>
                            <Box sx={{ ...classes.timeStack }}>
                                <TimeBox label={'Hrs'} value={timeLeft.hours} isHaleBrand={isHaleBrand} />
                                <Box sx={{ ...classes.timeDividorDot }}>:</Box>
                                <TimeBox label={'Mins'} value={timeLeft.minutes} isHaleBrand={isHaleBrand} />
                                <Box sx={{ ...classes.timeDividorDot }}>:</Box>
                                <TimeBox label={'Sec'} value={timeLeft.seconds} isHaleBrand={isHaleBrand} />
                            </Box>
                        </>
                    )}
                    {shouldShowButton && (
                        <Button
                            sx={{ ...classes.startTestBtn }}
                            onClick={handleTestAction}
                            disabled={isGettingToken}
                        >
                            {isGettingToken ? (
                                <CircularProgress 
                                    size={20} 
                                    sx={{ 
                                        color: 'white',
                                        '@media(max-width: 480px)': {
                                            width: '16px !important',
                                            height: '16px !important',
                                        },
                                    }} 
                                />
                            ) : (
                                buttonText
                            )}
                        </Button>
                    )}
                </Stack>
            </Box>
        </Box>
    );
};

type TimeBoxProps = {
    label: string;
    value: string;
    isHaleBrand?: boolean;
};

const TimeBox: React.FC<TimeBoxProps> = ({ label, value, isHaleBrand = false }) => {
    const theme = useTheme();
    const classes = styles(theme, isHaleBrand);

    return (
        <Box textAlign="center" sx={{ ...classes.timeValueBox }}>
            <Box sx={{ ...classes.timeValue }}>{value}</Box>
            <Typography variant="caption" color="text.secondary">
                {label}
            </Typography>
        </Box>
    );
};

export default TestCountdownBanner;
